import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    Button,
    TextField,
    Chip,
    IconButton,
    Tooltip,
    Stack,
    Collapse,
    useMediaQuery,
    useTheme,
    Divider,
    FormControlLabel
} from '@mui/material';
import {
    Send as SendIcon,
    KeyboardArrowDown,
    KeyboardArrowUp,
    Email as EmailIcon,
    WhatsApp as WhatsAppIcon,
    Sms as SmsIcon,
    Event as EventIcon,
    Person as PersonIcon,
    Gavel as GavelIcon,
    LocationOn as LocationIcon
} from '@mui/icons-material';

import configServ from '../../services/config';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';

const CaseNotificationPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { user_id, company_id, companyName } = useSelector((state) => state.GlobalVariables);


    // States
    const [selectedDate, setSelectedDate] = useState(
        new Date().toISOString().split("T")[0]
    );
    const [hearings, setHearings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedRows, setExpandedRows] = useState({});

    // Matrix state for selections
    const [selectedChannels, setSelectedChannels] = useState({});

    // Fetch Hearings & Reset
    const getHearingsByDate = async (date) => {
        setLoading(true);

        // Reset previous selections on new fetch
        setSelectedChannels({});
        setExpandedRows({});

        try {
            const data = {
                hearing_date: date || selectedDate,
                company_id: company_id,
            };

            const response = await configServ.getHearingsByDate(data);
            console.log("Hearings by date response:", response);

            const hearingData = response?.data || [];
            setHearings(hearingData);

            // Expand all by default
            const initialExpanded = {};
            hearingData.forEach((h) => {
                initialExpanded[h.id] = true;
            });
            setExpandedRows(initialExpanded);

        } catch (error) {
            console.error("Error fetching hearings by date:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getHearingsByDate(selectedDate);
    }, []);

    const handleDateChange = (e) => {
        const newDate = e.target.value;
        setSelectedDate(newDate);
        getHearingsByDate(newDate);
    };

    const toggleRow = (hearingId) => {
        setExpandedRows((prev) => ({ ...prev, [hearingId]: !prev[hearingId] }));
    };

    const getClientKey = (hearingId, caseId, clientId) => `${hearingId}_${caseId}_${clientId}`;

    // Channel Selection Logic
    const handleChannelToggle = (hearingId, caseId, clientId, channel) => {
        const key = getClientKey(hearingId, caseId, clientId);
        setSelectedChannels((prev) => {
            const current = prev[key] || [];
            const updated = current.includes(channel)
                ? current.filter((c) => c !== channel)
                : [...current, channel];

            return {
                ...prev,
                [key]: updated,
            };
        });
    };

    const handleSelectAllForClient = (hearing, client, isChecked) => {
        const key = getClientKey(hearing.id, hearing.case_id, client.id);
        if (!isChecked) {
            setSelectedChannels((prev) => ({ ...prev, [key]: [] }));
            return;
        }

        const availableChannels = [];
        if (client.can_send_email) availableChannels.push('Email');
        if (client.can_send_whatsapp) availableChannels.push('WhatsApp');
        if (client.can_send_sms) availableChannels.push('SMS');

        setSelectedChannels((prev) => ({
            ...prev,
            [key]: availableChannels,
        }));
    };

    const handleBulkChannelSelect = (channel, enable) => {
        const newSelections = { ...selectedChannels };

        hearings.forEach((hearing) => {
            hearing.clients?.forEach((client) => {
                const canSend =
                    (channel === 'Email' && client.can_send_email) ||
                    (channel === 'WhatsApp' && client.can_send_whatsapp) ||
                    (channel === 'SMS' && client.can_send_sms);

                if (canSend) {
                    const key = getClientKey(hearing.id, hearing.case_id, client.id);
                    const currentList = newSelections[key] || [];

                    if (enable) {
                        if (!currentList.includes(channel)) {
                            newSelections[key] = [...currentList, channel];
                        }
                    } else {
                        newSelections[key] = currentList.filter((c) => c !== channel);
                    }
                }
            });
        });

        setSelectedChannels(newSelections);
    };

    // Final Payload Construction
    const handleSendNotification = async () => {
        try {
            const notifications = [];

            Object.entries(selectedChannels).forEach(([key, channels]) => {
                if (Array.isArray(channels) && channels.length > 0) {
                    const [hearingId, caseId, clientId] = key
                        .split("_")
                        .map(Number);

                    notifications.push({
                        hearing_id: hearingId,
                        case_id: caseId,
                        client_id: clientId,
                        channels,
                    });
                }
            });

            if (notifications.length === 0) {
                toast.error("Please select at least one notification channel.");
                return;
            }

            const payload = {
                company_id,
                created_by: user_id,
                notifications,
            };

            console.log("=== FINAL PAYLOAD TO BACKEND ===");
            console.log(payload);

            const response = await configServ.createNotificationBatch(payload);

            if (response?.status) {
                toast.success(response.message || "Notifications queued successfully!");
                // Optionally, you can reset selections after successful submission
                setSelectedChannels({});
            } else {
                toast.error(response?.message || "Failed to queue notifications.");
            }
        } catch (error) {
            console.error("Notification Error:", error);
            toast.error(
                error?.response?.data?.message || "Something went wrong."
            );
        }
    };

    const totalSelectedClients = useMemo(() => {
        return Object.values(selectedChannels).filter((ch) => ch && ch.length > 0).length;
    }, [selectedChannels]);

    return (
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, backgroundColor: '#f4f6f8', minHeight: '100vh', width: '100%' }}>

            {/* Header Controls */}
            <Card sx={{ mb: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'stretch', md: 'center' }}
                        spacing={2}
                    >
                        <Box>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold" color="primary.main">
                                    Case Notifications
                                </Typography>
                                <Chip
                                    label={`Total Cases: ${hearings.length}`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontWeight: 'bold' }}
                                />
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                                Select date and choose client communication channels
                            </Typography>
                        </Box>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="stretch">
                            <TextField
                                label="Hearing Date"
                                type="date"
                                size="small"
                                fullWidth={isMobile}
                                value={selectedDate}
                                onChange={handleDateChange}
                                InputLabelProps={{ shrink: true }}
                            />
                            <Button
                                variant="outlined"
                                startIcon={<EventIcon />}
                                onClick={() => getHearingsByDate(selectedDate)}
                                fullWidth={isMobile}
                            >
                                Fetch
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                startIcon={<SendIcon />}
                                disabled={totalSelectedClients === 0}
                                onClick={handleSendNotification}
                                fullWidth={isMobile}
                                sx={{ fontWeight: 'bold' }}
                            >
                                Send ({totalSelectedClients})
                            </Button>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>

            {/* Global Selection Helpers */}
            <Paper sx={{ p: 1.5, mb: 2, overflowX: 'auto' }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap={isMobile ? 'wrap' : 'nowrap'} gap={1}>
                    <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ minWidth: '70px' }}>
                        Quick All:
                    </Typography>
                    <Chip
                        icon={<EmailIcon fontSize="small" />}
                        label="Email"
                        size="small"
                        onClick={() => handleBulkChannelSelect('Email', true)}
                        onDelete={() => handleBulkChannelSelect('Email', false)}
                        color="primary"
                        variant="outlined"
                        clickable
                    />
                    <Chip
                        icon={<WhatsAppIcon fontSize="small" />}
                        label="WhatsApp"
                        size="small"
                        onClick={() => handleBulkChannelSelect('WhatsApp', true)}
                        onDelete={() => handleBulkChannelSelect('WhatsApp', false)}
                        color="success"
                        variant="outlined"
                        clickable
                    />
                    <Chip
                        icon={<SmsIcon fontSize="small" />}
                        label="SMS"
                        size="small"
                        onClick={() => handleBulkChannelSelect('SMS', true)}
                        onDelete={() => handleBulkChannelSelect('SMS', false)}
                        color="warning"
                        variant="outlined"
                        clickable
                    />
                </Stack>
            </Paper>

            {/* CONDITIONAL RENDER: Mobile Cards vs Desktop Table */}
            {hearings.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">No hearings available for this date.</Typography>
                </Paper>
            ) : isMobile ? (

                /* MOBILE VIEW WITH S.NO. */
                <Stack spacing={2}>
                    {hearings.map((hearing, index) => {
                        const isExpanded = !!expandedRows[hearing.id];
                        return (
                            <Card key={hearing.id} variant="outlined" sx={{ borderRadius: 2, backgroundColor: '#fff' }}>
                                <Box
                                    sx={{ p: 1.5, backgroundColor: '#1976d2', color: '#fff', cursor: 'pointer' }}
                                    onClick={() => toggleRow(hearing.id)}
                                >
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Chip
                                                label={`#${index + 1}`}
                                                size="small"
                                                sx={{ backgroundColor: '#fff', color: '#1976d2', fontWeight: 'bold' }}
                                            />
                                            <Box>
                                                <Typography variant="subtitle1" fontWeight="bold">
                                                    Case: {hearing.case_no}
                                                </Typography>
                                                <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }}>
                                                    FIR: {hearing.fir_no}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                        <IconButton size="small" sx={{ color: '#fff' }}>
                                            {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                        </IconButton>
                                    </Stack>
                                </Box>

                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                    <Stack spacing={0.5} sx={{ mb: 1.5, fontSize: '0.85rem' }}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <LocationIcon fontSize="inherit" color="action" />
                                            <Typography variant="body2">{hearing.court_name} ({hearing.court_room})</Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <GavelIcon fontSize="inherit" color="action" />
                                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>Judge: {hearing.judge_name}</Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <PersonIcon fontSize="inherit" color="action" />
                                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>Vs: {hearing.opposite_party}</Typography>
                                        </Stack>
                                    </Stack>

                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                        <Divider sx={{ my: 1.5 }} />
                                        <Typography variant="subtitle2" fontWeight="bold" color="primary.main" sx={{ mb: 1 }}>
                                            Clients ({hearing.clients?.length || 0})
                                        </Typography>

                                        <Stack spacing={1.5}>
                                            {hearing.clients?.map((client) => {
                                                const key = getClientKey(hearing.id, hearing.case_id, client.id);
                                                const currentSelected = selectedChannels[key] || [];

                                                return (
                                                    <Paper key={client.id} variant="outlined" sx={{ p: 1.5, backgroundColor: '#f8fafc' }}>
                                                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                                                            <Box>
                                                                <Typography variant="subtitle2" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                                                                    {client.client_name}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary" display="block">
                                                                    {client.phone} | {client.email}
                                                                </Typography>
                                                            </Box>

                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        size="small"
                                                                        checked={currentSelected.length > 0}
                                                                        onChange={(e) => handleSelectAllForClient(hearing, client, e.target.checked)}
                                                                    />
                                                                }
                                                                label={<Typography variant="caption">All</Typography>}
                                                                sx={{ m: 0 }}
                                                            />
                                                        </Stack>

                                                        <Stack direction="row" justifyContent="space-around" sx={{ pt: 0.5, borderTop: '1px dashed #e2e8f0' }}>
                                                            <Tooltip title={!client.can_send_email ? "Disabled" : ""}>
                                                                <FormControlLabel
                                                                    control={
                                                                        <Checkbox
                                                                            size="small"
                                                                            color="primary"
                                                                            disabled={!client.can_send_email}
                                                                            checked={currentSelected.includes('Email')}
                                                                            onChange={() => handleChannelToggle(hearing.id, hearing.case_id, client.id, 'Email')}
                                                                        />
                                                                    }
                                                                    label={<Typography variant="caption">Email</Typography>}
                                                                />
                                                            </Tooltip>

                                                            <Tooltip title={!client.can_send_whatsapp ? "Disabled" : ""}>
                                                                <FormControlLabel
                                                                    control={
                                                                        <Checkbox
                                                                            size="small"
                                                                            color="success"
                                                                            disabled={!client.can_send_whatsapp}
                                                                            checked={currentSelected.includes('WhatsApp')}
                                                                            onChange={() => handleChannelToggle(hearing.id, hearing.case_id, client.id, 'WhatsApp')}
                                                                        />
                                                                    }
                                                                    label={<Typography variant="caption">WA</Typography>}
                                                                />
                                                            </Tooltip>

                                                            <Tooltip title={!client.can_send_sms ? "Disabled" : ""}>
                                                                <FormControlLabel
                                                                    control={
                                                                        <Checkbox
                                                                            size="small"
                                                                            color="warning"
                                                                            disabled={!client.can_send_sms}
                                                                            checked={currentSelected.includes('SMS')}
                                                                            onChange={() => handleChannelToggle(hearing.id, hearing.case_id, client.id, 'SMS')}
                                                                        />
                                                                    }
                                                                    label={<Typography variant="caption">SMS</Typography>}
                                                                />
                                                            </Tooltip>
                                                        </Stack>
                                                    </Paper>
                                                );
                                            })}
                                        </Stack>
                                    </Collapse>
                                </CardContent>
                            </Card>
                        );
                    })}
                </Stack>

            ) : (

                /* DESKTOP VIEW WITH S.NO. */
                <TableContainer component={Paper} sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                    <Table>
                        <TableHead sx={{ backgroundColor: '#1976d2' }}>
                            <TableRow>
                                <TableCell width="40px" />
                                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }} width="60px">S.No.</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Case Info</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Court / Room</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Judge Name</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Opposite Party</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Total Clients</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {hearings.map((hearing, index) => {
                                const isExpanded = !!expandedRows[hearing.id];
                                return (
                                    <React.Fragment key={hearing.id}>
                                        <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                                            <TableCell>
                                                <IconButton size="small" onClick={() => toggleRow(hearing.id)}>
                                                    {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                                </IconButton>
                                            </TableCell>
                                            {/* S.No. Cell */}
                                            <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                                                {index + 1}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="subtitle2" fontWeight="bold">
                                                    Case No: {hearing.case_no}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    FIR: {hearing.fir_no} | Filed: {hearing.filing_date}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{hearing.court_name}</Typography>
                                                <Typography variant="caption" color="text.secondary">{hearing.court_room}</Typography>
                                            </TableCell>
                                            <TableCell sx={{ textTransform: 'capitalize' }}>{hearing.judge_name}</TableCell>
                                            <TableCell sx={{ textTransform: 'capitalize' }}>{hearing.opposite_party}</TableCell>
                                            <TableCell>
                                                <Chip label={`${hearing.clients?.length || 0} Clients`} size="small" color="info" />
                                            </TableCell>
                                        </TableRow>

                                        {/* Expandable Sub-Table */}
                                        <TableRow>
                                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                    <Box sx={{ margin: 2, p: 2, backgroundColor: '#f8fafc', borderRadius: 2 }}>
                                                        <Typography variant="subtitle2" gutterBottom fontWeight="bold" color="text.secondary">
                                                            Client Contact & Notification Channels
                                                        </Typography>
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell width="40px"><b>All</b></TableCell>
                                                                    <TableCell><b>Client Name</b></TableCell>
                                                                    <TableCell><b>Contact Details</b></TableCell>
                                                                    <TableCell align="center"><b>Email</b></TableCell>
                                                                    <TableCell align="center"><b>WhatsApp</b></TableCell>
                                                                    <TableCell align="center"><b>SMS</b></TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {hearing.clients?.map((client) => {
                                                                    const key = getClientKey(hearing.id, hearing.case_id, client.id);
                                                                    const currentSelected = selectedChannels[key] || [];

                                                                    return (
                                                                        <TableRow key={client.id} hover>
                                                                            <TableCell>
                                                                                <Checkbox
                                                                                    size="small"
                                                                                    checked={currentSelected.length > 0}
                                                                                    onChange={(e) => handleSelectAllForClient(hearing, client, e.target.checked)}
                                                                                />
                                                                            </TableCell>
                                                                            <TableCell sx={{ textTransform: 'capitalize', fontWeight: 500 }}>
                                                                                {client.client_name}
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                <Typography variant="caption" display="block">{client.email}</Typography>
                                                                                <Typography variant="caption" color="text.secondary">{client.phone}</Typography>
                                                                            </TableCell>

                                                                            <TableCell align="center">
                                                                                <Tooltip title={!client.can_send_email ? "Email disabled" : ""}>
                                                                                    <span>
                                                                                        <Checkbox
                                                                                            size="small"
                                                                                            color="primary"
                                                                                            disabled={!client.can_send_email}
                                                                                            checked={currentSelected.includes('Email')}
                                                                                            onChange={() => handleChannelToggle(hearing.id, hearing.case_id, client.id, 'Email')}
                                                                                        />
                                                                                    </span>
                                                                                </Tooltip>
                                                                            </TableCell>

                                                                            <TableCell align="center">
                                                                                <Tooltip title={!client.can_send_whatsapp ? "WhatsApp disabled" : ""}>
                                                                                    <span>
                                                                                        <Checkbox
                                                                                            size="small"
                                                                                            color="success"
                                                                                            disabled={!client.can_send_whatsapp}
                                                                                            checked={currentSelected.includes('WhatsApp')}
                                                                                            onChange={() => handleChannelToggle(hearing.id, hearing.case_id, client.id, 'WhatsApp')}
                                                                                        />
                                                                                    </span>
                                                                                </Tooltip>
                                                                            </TableCell>

                                                                            <TableCell align="center">
                                                                                <Tooltip title={!client.can_send_sms ? "SMS disabled" : ""}>
                                                                                    <span>
                                                                                        <Checkbox
                                                                                            size="small"
                                                                                            color="warning"
                                                                                            disabled={!client.can_send_sms}
                                                                                            checked={currentSelected.includes('SMS')}
                                                                                            onChange={() => handleChannelToggle(hearing.id, hearing.case_id, client.id, 'SMS')}
                                                                                        />
                                                                                    </span>
                                                                                </Tooltip>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    );
                                                                })}
                                                            </TableBody>
                                                        </Table>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default CaseNotificationPage;